import i18n from "i18next";
import type { ClipboardEvent } from "react";
import { stripAuthorWrapper, type AuthorsReading, type AuthorsStatus } from "@/lib/titleAuthors";
import { testIds } from "@/testIds";
import styles from "./TitleEditContent.module.scss";

const T = testIds.titleEdit;

/** Why Fields cannot be chosen, or null when it can. */
function blockedReason(status: AuthorsStatus): string | null {
  if (status.kind === "foreign") return `These authors use ${status.command}, which Lilia can't show as fields yet.`;
  if (status.kind === "unbalanced") return `${braceProblem(status.brace)} Close it to see fields.`;
  return null;
}

const braceProblem = (brace: "{" | "}") => (brace === "{" ? "A { isn't closed." : "A } has no { to close.");

/** Fields · Edit as LaTeX, on the Authors header. It swaps the authors only. */
export function ModeSwitch({
  mode,
  status,
  onChange,
}: {
  mode: "fields" | "latex";
  status: AuthorsStatus;
  onChange: (m: "fields" | "latex") => void;
}) {
  const reason = blockedReason(status);
  return (
    <div className={styles.segmented} role="group" aria-label={i18n.t("editor:titleEdit.authorsEditingMode")}>
      <span title={reason ?? undefined}>
        <button
          type="button"
          data-testid={T.modeFields}
          aria-pressed={mode === "fields"}
          disabled={reason !== null}
          title={reason ?? undefined}
          onClick={() => onChange("fields")}
        >
          {i18n.t("editor:titleEdit.fields")}
        </button>
      </span>
      <button type="button" data-testid={T.modeLatex} aria-pressed={mode === "latex"} onClick={() => onChange("latex")}>
        {i18n.t("editor:titleEdit.editAsLatex")}
      </button>
    </div>
  );
}

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;

function StatusLine({ reading }: { reading: AuthorsReading }) {
  const { status } = reading;
  if (status.kind === "foreign") {
    return (
      <>
        <strong>{i18n.t("editor:titleEdit.theseAuthorsStayInLatex")}</strong> They use {status.command}, which Lilia can&apos;t show as fields
        yet. Nothing is changed: they&apos;re saved and exported exactly as written.
      </>
    );
  }
  if (status.kind === "unbalanced") {
    return (
      <>
        <strong>{braceProblem(status.brace)}</strong> {i18n.t("editor:titleEdit.closeItToSeeThese")}
      </>
    );
  }
  return (
    <>
      Reads as {plural(reading.authors.length, "author")} and {plural(reading.noteCount, "note")}. Switch to Fields any
      time.
    </>
  );
}

/** The contents of \author{...}, the wrapper drawn as fixed gutter text. */
export function LatexBox({
  value,
  reading,
  onChange,
}: {
  value: string;
  reading: AuthorsReading;
  onChange: (value: string) => void;
}) {
  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const inner = stripAuthorWrapper(e.clipboardData.getData("text"));
    if (inner === null) return;
    e.preventDefault();
    const el = e.currentTarget;
    onChange(el.value.slice(0, el.selectionStart) + inner + el.value.slice(el.selectionEnd));
  };
  return (
    <>
      <div className={styles.latexBox}>
        <span className={styles.gutter} aria-hidden="true">
          {"\\author{"}
        </span>
        <textarea
          data-testid={T.latex}
          aria-label={i18n.t("editor:titleEdit.authorsAsLatex")}
          className={styles.latexArea}
          rows={6}
          spellCheck={false}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onPaste={onPaste}
        />
        <span className={styles.gutter} aria-hidden="true">
          {"}"}
        </span>
      </div>
      <p className={styles.hint} data-testid={T.latexStatus} data-state={reading.status.kind}>
        <StatusLine reading={reading} />
      </p>
    </>
  );
}
